import React, { useState, useRef } from 'react';
import { Button, Modal, Message, Form } from 'semantic-ui-react';
import { assessmentPeriodMetricsApi, BulkAssessmentPeriodMetricRequest } from '../api/assessmentPeriodMetricsApi';
import { BulkPerformanceMetricRequest } from '../api/performanceMetricsApi';
import { validateMetricValue } from './MetricTypeSelector';
import Papa from 'papaparse';

interface MetricImportProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CSVRow {
  metric_id: string;
  assessment_period_id: string;
  value: string;
  target_value?: string;
  notes?: string;
  data_source?: string;
  collected_date?: string;
}

export const MetricImport: React.FC<MetricImportProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<BulkAssessmentPeriodMetricRequest['metrics']>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [bulkCreate, { isLoading: isImporting }] = assessmentPeriodMetricsApi.useBulkCreateAssessmentPeriodMetricsMutation();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setErrors(['Please select a CSV file']);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setPreviewMode(false);
    setImportData([]);

    Papa.parse(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedRows = results.data as CSVRow[];
        const validatedData: BulkAssessmentPeriodMetricRequest['metrics'] = [];
        const validationErrors: string[] = [];

        parsedRows.forEach((row, index) => {
          const rowNum = index + 2; // +2 because index is 0-based and CSV has header
          const rowErrors: string[] = [];

          // Validate required fields
          if (!row.metric_id) {
            rowErrors.push(`Row ${rowNum}: metric_id is required`);
          }
          if (!row.assessment_period_id) {
            rowErrors.push(`Row ${rowNum}: assessment_period_id is required`);
          }
          if (!row.value) {
            rowErrors.push(`Row ${rowNum}: value is required`);
          }

          // Validate metric_id
          const metricIdNum = parseInt(row.metric_id);
          if (isNaN(metricIdNum) || metricIdNum < 1) {
            rowErrors.push(`Row ${rowNum}: metric_id must be a valid positive number`);
          }

          // Validate assessment_period_id
          const assessmentPeriodIdNum = parseInt(row.assessment_period_id);
          if (isNaN(assessmentPeriodIdNum) || assessmentPeriodIdNum < 1) {
            rowErrors.push(`Row ${rowNum}: assessment_period_id must be a valid positive number`);
          }

          // Validate value
          const valueNum = parseFloat(row.value);
          if (isNaN(valueNum)) {
            rowErrors.push(`Row ${rowNum}: value must be a number`);
          }

          // Validate target_value if provided
          let targetValueNum: number | null = null;
          if (row.target_value) {
            targetValueNum = parseFloat(row.target_value);
            if (isNaN(targetValueNum)) {
              rowErrors.push(`Row ${rowNum}: target_value must be a number`);
            }
          }

          if (rowErrors.length > 0) {
            validationErrors.push(...rowErrors);
          } else {
            validatedData.push({
              metric_id: metricIdNum,
              assessment_period_id: assessmentPeriodIdNum,
              value: valueNum,
              target_value: targetValueNum,
              notes: row.notes?.trim() || null,
              data_source: row.data_source?.trim() || null,
              collected_date: row.collected_date || null,
            });
          }
        });

        if (validationErrors.length > 0) {
          setErrors(validationErrors);
        } else {
          setImportData(validatedData);
          setPreviewMode(true);
        }
      },
      error: (error) => {
        setErrors([`Error parsing CSV: ${error.message}`]);
      },
    });
  };

  const handleImport = async () => {
    if (importData.length === 0) return;

    try {
      const result = await bulkCreate({ metrics: importData }).unwrap();
      if (result.errors && result.errors.length > 0) {
        const errorMessages = result.errors.map(err => 
          `Row ${err.row}: ${err.error || JSON.stringify(err.errors)}`
        );
        setErrors(errorMessages);
      } else {
        onSuccess();
        onClose();
        resetForm();
      }
    } catch (err: any) {
      console.error('Import failed:', err);
      setErrors([err?.data?.error || 'Failed to import metrics']);
    }
  };

  const resetForm = () => {
    setFile(null);
    setImportData([]);
    setErrors([]);
    setPreviewMode(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const downloadTemplate = () => {
    const headers = [
      'metric_id',
      'assessment_period_id',
      'value',
      'target_value',
      'notes',
      'data_source',
      'collected_date',
    ];
    
    const exampleRow = [
      '1',           // metric_id (required) - ID of metric definition
      '1',           // assessment_period_id (required)
      '45.0',        // value (required)
      '50.0',        // target_value (optional)
      'From survey', // notes (optional)
      'Qualtrics',   // data_source (optional)
      '2024-01-15',  // collected_date (optional, YYYY-MM-DD format)
    ];

    const csvContent = [
      headers.join(','),
      exampleRow.join(','),
      '',
      'Notes:',
      '- metric_id: ID of the metric definition (create definitions in "Metric Definitions" tab first)',
      '- assessment_period_id: ID of the assessment period',
      '- Dates should be in YYYY-MM-DD format',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'performance_metrics_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal open={open} onClose={handleClose} size="large">
      <Modal.Header>Import Performance Metrics</Modal.Header>
      <Modal.Content>
        {!previewMode ? (
          <div>
            <Message info>
              <Message.Header>CSV Import Instructions</Message.Header>
              <Message.List>
                <Message.Item>Download the template CSV file to see the required format</Message.Item>
                <Message.Item>Required columns: metric_id, assessment_period_id, value</Message.Item>
                <Message.Item>Optional columns: target_value, notes, data_source, collected_date</Message.Item>
                <Message.Item>Note: metric_id refers to a metric definition. Create metric definitions in the "Metric Definitions" tab first.</Message.Item>
              </Message.List>
            </Message>

            <div style={{ marginBottom: '20px' }}>
              <Button onClick={downloadTemplate} icon="download" content="Download Template CSV" />
            </div>

            <Form>
              <Form.Field>
                <label>Select CSV File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ padding: '8px' }}
                />
              </Form.Field>
            </Form>

            {errors.length > 0 && (
              <Message negative>
                <Message.Header>Validation Errors</Message.Header>
                <Message.List>
                  {errors.map((error, index) => (
                    <Message.Item key={index}>{error}</Message.Item>
                  ))}
                </Message.List>
              </Message>
            )}
          </div>
        ) : (
          <div>
            <Message success>
              <Message.Header>Preview: {importData.length} metrics ready to import</Message.Header>
              <p>Review the data below and click Import to proceed.</p>
            </Message>

            <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Metric ID</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Period ID</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Value</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Target</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {importData.slice(0, 50).map((metric, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px' }}>{metric.metric_id}</td>
                      <td style={{ padding: '8px' }}>{metric.assessment_period_id}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{metric.value}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{metric.target_value || '—'}</td>
                      <td style={{ padding: '8px' }}>{metric.data_source || '—'}</td>
                    </tr>
                  ))}
                  {importData.length > 50 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '8px', textAlign: 'center', color: '#666' }}>
                        ... and {importData.length - 50} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {errors.length > 0 && (
              <Message negative style={{ marginTop: '20px' }}>
                <Message.Header>Import Errors</Message.Header>
                <Message.List>
                  {errors.map((error, index) => (
                    <Message.Item key={index}>{error}</Message.Item>
                  ))}
                </Message.List>
              </Message>
            )}
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isImporting}>
          {previewMode ? 'Cancel' : 'Close'}
        </Button>
        {previewMode && (
          <>
            <Button onClick={() => setPreviewMode(false)} disabled={isImporting}>
              Back
            </Button>
            <Button
              primary
              onClick={handleImport}
              loading={isImporting}
              disabled={importData.length === 0}
            >
              Import {importData.length} Metrics
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};
